import React, {useMemo} from 'react'
import {useSelector} from 'react-redux'
import styled from '@emotion/styled'
import {Button} from '@vio/atlas/Button'
import {Divider} from '@vio/atlas/Divider'
import {Box} from '@vio/atlas/helpers/Box'
import {TitleBar} from '@vio/atlas/helpers/TitleBar'
import {Icon} from '@vio/atlas/Icon'
import {cssTheme} from '@vio/atlas/themes'

import FormattedMessageWrapper from '@daedalus/core/src/localization/components/FormattedMessage'

import {getError} from '../../modules/selector'
import {BookingCreation} from '../BookingCreation'
import {ErrorAlert} from '../ErrorMessage/ErrorAlert'
import {FreezeOverlay} from '../FreezeOverlay'
import {SimplifiedHotelCard} from '../SimplifiedHotelCard'
import {FreezeCTA} from './FreezeCTA'
import {FreezeDetailsHeader} from './FreezeDetailsHeader'
import {OfferCheck} from './OfferCheck'
import {HotelCardSkeleton} from './SkeletonLoading'

const ScrollableWrapper = styled.div`
  height: calc(100vh - 257px);
  overflow: auto;
`

const CtaWrapper = styled.div`
  position: absolute;
  bottom: 0;
  right: 0;
  width: 100%;
  z-index: 2;
  background-color: ${cssTheme.colors.background.neutral.c000};
  overflow: hidden;
  box-shadow: ${cssTheme.shadows.fixedReverse};
`

const CloseButton = styled(Button)`
  opacity: 0.6;
`

interface Props {
  offerLink: string | undefined
  isOpen: boolean
  isPageLoading: boolean
  isCreateBookingLoading: boolean
  hideOverlay: () => void
  handleCreateFreezeBooking: () => void
}

export const FreezeDetailsDesktop: React.FC<Props> = ({
  isOpen,
  offerLink,
  isCreateBookingLoading,
  isPageLoading,
  hideOverlay,
  handleCreateFreezeBooking
}) => {
  const {errored} = useSelector(getError)

  const Content = useMemo(() => {
    if (isPageLoading) {
      return (
        <ScrollableWrapper>
          <Box paddingRight="s700" paddingLeft="s700" paddingTop="s500">
            <HotelCardSkeleton />
          </Box>
        </ScrollableWrapper>
      )
    }

    if (isCreateBookingLoading) {
      return (
        <ScrollableWrapper>
          <BookingCreation />
        </ScrollableWrapper>
      )
    }

    return (
      <ScrollableWrapper>
        <Box paddingRight="s700" paddingLeft="s700" paddingTop="s500">
          <Box display="flex" flexDirection="column" gap="s400">
            <ErrorAlert />
            <SimplifiedHotelCard />
          </Box>
        </Box>
      </ScrollableWrapper>
    )
  }, [isCreateBookingLoading, isPageLoading])

  return (
    <FreezeOverlay isOpen={isOpen} onClose={hideOverlay}>
      <TitleBar
        endContent={
          <CloseButton
            variant="quiet"
            iconStart={<Icon size="md" name={'Close'} />}
            onClick={hideOverlay}
          />
        }
      />
      <Divider />
      {!isCreateBookingLoading && (
        <Box paddingTop="s600" paddingRight="s700" paddingLeft="s700">
          <FreezeDetailsHeader isLoading={isPageLoading} alignment="left" />
        </Box>
      )}
      {offerLink && <OfferCheck offerLink={offerLink} />}
      {Content}

      {!isCreateBookingLoading && !errored && (
        <CtaWrapper>
          <Divider />
          <Box
            paddingLeft="s700"
            paddingRight="s700"
            paddingTop="s500"
            paddingBottom="s700"
          >
            <FreezeCTA
              isPageLoading={isPageLoading}
              handleCreateFreezeBooking={handleCreateFreezeBooking}
              ctaTitleText={
                <FormattedMessageWrapper
                  id="dealFreeze.oneTapToFreeze"
                  defaultMessage="One tap to freeze – zero obligation to book."
                />
              }
            />
          </Box>
        </CtaWrapper>
      )}
    </FreezeOverlay>
  )
}
